// Two-step "verify your email, claim credits" card shown on /credits to
// signed-up users who have no email on file and haven't claimed the bonus.
// Step 1 mails a 6-digit code (POST /account/email/claim), step 2 verifies it
// and grants the one-time promo (POST /account/email/confirm).

import { useState } from 'react';
import {
  Alert,
  Button,
  CircularProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailReadRounded';
import { useAuth } from '../auth/AuthProvider';
import { format, useTranslation } from '../i18n/I18nProvider';
import { PROMO_CREDITS } from '../lib/promo';

export default function EmailVerifyPromoCard() {
  const { user, refresh } = useAuth();
  const t = useTranslation();

  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'code' | 'done'>('email');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [grantedCredits, setGrantedCredits] = useState<number | null>(null);

  // Eligibility mirrors the server guards (anonymous users are 403'd there).
  if (!user || user.isAnonymous || user.email || user.emailPromoGranted) return null;

  const sendCode = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/account/email/claim', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim() }),
      });
      const body = (await res.json().catch(() => null)) as { error?: string } | null;
      if (!res.ok) throw new Error(body?.error || t.emailPromo.errorSend);
      setStep('code');
    } catch (err) {
      setError(err instanceof Error ? err.message : t.emailPromo.errorSend);
    } finally {
      setBusy(false);
    }
  };

  const confirm = async () => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch('/api/v1/account/email/confirm', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: email.trim(), code: code.trim() }),
      });
      const body = (await res.json().catch(() => null)) as
        | { error?: string; granted?: boolean; credits?: number }
        | null;
      if (!res.ok) throw new Error(body?.error || t.emailPromo.errorVerify);
      setGrantedCredits(body?.granted ? (body.credits ?? PROMO_CREDITS.EMAIL_VERIFY) : 0);
      setStep('done');
      void refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : t.emailPromo.errorVerify);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 3, border: '1px solid', borderColor: 'primary.main' }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
        <MarkEmailReadIcon color="primary" />
        <Typography sx={{ fontWeight: 700 }}>
          {format(t.emailPromo.cardTitle, { credits: PROMO_CREDITS.EMAIL_VERIFY })}
        </Typography>
      </Stack>

      {step === 'done' ? (
        <Alert severity={grantedCredits ? 'success' : 'info'}>
          {grantedCredits
            ? format(t.emailPromo.success, { credits: grantedCredits })
            : t.emailPromo.alreadyClaimed}
        </Alert>
      ) : (
        <>
          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
            {format(t.emailPromo.cardBody, { credits: PROMO_CREDITS.EMAIL_VERIFY })}
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {step === 'email' ? (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
              <TextField
                size="small"
                type="email"
                label={t.emailPromo.emailLabel}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                sx={{ minWidth: 260 }}
              />
              <Button
                variant="contained"
                onClick={sendCode}
                disabled={busy || !email.trim()}
                startIcon={busy ? <CircularProgress size={14} /> : undefined}
              >
                {busy ? t.emailPromo.sending : t.emailPromo.sendCode}
              </Button>
            </Stack>
          ) : (
            <Stack spacing={1.5}>
              <Typography variant="body2">
                {format(t.emailPromo.codeHint, { email: email.trim() })}
              </Typography>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
                <TextField
                  size="small"
                  label={t.emailPromo.codeLabel}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  inputProps={{ inputMode: 'numeric', maxLength: 8 }}
                  sx={{ minWidth: 180 }}
                />
                <Button
                  variant="contained"
                  onClick={confirm}
                  disabled={busy || !code.trim()}
                  startIcon={busy ? <CircularProgress size={14} /> : undefined}
                >
                  {busy ? t.emailPromo.confirming : t.emailPromo.confirm}
                </Button>
              </Stack>
            </Stack>
          )}
        </>
      )}
    </Paper>
  );
}
